import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Shield, KeyRound, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SetPassword() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd !== confirm) { setError("The two passwords don't match."); return; }
    if (pwd.length < 12) { setError("Password must be at least 12 characters. Three random words are a great, memorable choice."); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { new_password: pwd });
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated");
      nav(user?.role === "admin" ? "/admin" : "/app", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)] text-white p-6" data-testid="set-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-7 h-7 bg-white flex items-center justify-center"><Shield className="w-4 h-4 text-black" /></div>
          <span className="font-display font-extrabold text-lg">TARDIGRADE</span>
        </div>
        <h2 className="font-display font-bold text-2xl mb-1">Set your own password</h2>
        <p className="text-[var(--txt-muted)] mb-5" style={{ fontSize: "16px" }}>
          For security, please replace your temporary password before continuing.
        </p>

        <div className="elevated p-3 mb-5 text-sm text-[var(--txt-secondary)] flex gap-2" data-testid="password-guidance">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-[var(--txt-muted)]" />
          <span>Tip: <strong>three random words</strong> make a strong, memorable password (e.g. <span className="font-mono">coffee-garden-rocket</span>). At least 12 characters. Don't reuse your temporary one.</span>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <PwdField label="New password" value={pwd} onChange={setPwd} show={show} setShow={setShow} testid="new-password" />
          <PwdField label="Confirm new password" value={confirm} onChange={setConfirm} show={show} setShow={setShow} testid="confirm-password" />
          {error && <p data-testid="set-password-error" className="text-[var(--urgent)]" style={{ fontSize: "16px" }}>{error}</p>}
          <button data-testid="set-password-submit" disabled={loading} className="w-full bg-white text-black py-3 font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Save and continue
          </button>
        </form>
      </div>
    </div>
  );
}

function PwdField({ label, value, onChange, show, setShow, testid }) {
  return (
    <label className="block">
      <span className="overline block mb-2" style={{ fontSize: "16px" }}>{label}</span>
      <div className="relative">
        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--txt-muted)] pointer-events-none" />
        <input data-testid={testid} type={show ? "text" : "password"} value={value} required placeholder="••••••••"
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[var(--bg-elevated)] pl-9 pr-10 py-3 outline-none text-white placeholder:text-[var(--txt-muted)]" />
        <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--txt-muted)] hover:text-white transition-colors">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}
